/**
 * Country Picker — Figma `01/ Entry/ Country Picker` (node 37:6397),
 * `Country/Tile` component (29:43 available / 29:59 current / 29:78 locked).
 *
 * Measured: 320×180 tiles in a 2-column grid with 24px gutters, `bg/surface`,
 * `radius/md`; the current tile takes a 1.5px `accent/indigo` border and a
 * "Current" pill; the locked tile is `bg/subtle` with a DASHED `border/default`,
 * a lock glyph, its body at 40% opacity, and the words "No access". The header is
 * the GS mark + `H1/24` "Choose your company" in white + a `Caption/12` subtitle
 * at 68% white; the footer is a `Caption/12` note at 62% white plus a ghost
 * "Log out".
 *
 * ===========================================================================
 * THE THREE BRANCHES, AND THE ONE THE DESIGN CANNOT DRAW
 * ===========================================================================
 *  · **exactly one accessible country** → this screen never renders. A picker
 *    with one choice is a dialog that asks a question with one answer, so the
 *    user is sent straight in. This is the common case for both pilot teams.
 *  · **`grp:true`** → all five, none locked.
 *  · **zero accessible countries and not `grp`** → `NoCompanyAccess` below.
 *
 * ===========================================================================
 * TWO CORRECTIONS TO THE DESIGN'S COPY. BOTH WERE MANDATED BY THE P2-T0 GATE.
 * ===========================================================================
 * 1. Figma's subtitle is the literal string **"You have access to three of four"**.
 *    It is hardcoded, it is wrong (there are FIVE countries — the design has no CD
 *    tile at all), and it would keep saying "three of four" to a pilot user with
 *    access to one of five. It is computed here from the token's own scopes.
 *
 * 2. Figma uses **AO — Angola, one of the two pilot countries this phase ships
 *    for — as the locked "No access" example.** Nothing here hardcodes which
 *    country is locked: `locked` is derived per tile from `accessibleCountries`,
 *    so an Angola coordinator sees Angola unlocked and a CD coordinator sees CD
 *    unlocked, whatever the mock showed. The design could not depict either pilot;
 *    the data can.
 *
 * ASSET NOTE (design-to-code G5): the lock and tick glyphs are exported assets in
 * Figma whose URLs expire in ~7 days. lucide's `Lock` and `Check` match them
 * exactly and lucide-react is this project's declared icon source, so the icon
 * components are used and the geometry (14×16 lock, 9×5.8 tick) is preserved by
 * explicit sizing.
 */

import { Navigate, useNavigate } from 'react-router-dom';
import { ArrowRight, Lock } from 'lucide-react';
import { COUNTRY_ORDER, COUNTRIES, currencyChip } from '../domain/countries';
import type { CountryCode, CountryProfile } from '../domain/countries';
import { useSession } from './session';
import { EntryBackdrop, G1Mark } from '../components/EntryBackdrop';
import { Button, Chip } from '../components/ui';
import { cn } from '../lib/cn';

/* ─────────────────────────────────────────────────────────────────── tile ── */

interface CountryTileProps {
  profile: CountryProfile;
  locked: boolean;
  current: boolean;
  onEnter: (code: CountryCode) => void;
}

function CountryTile({ profile, locked, current, onEnter }: CountryTileProps): React.JSX.Element {
  const body = (
    <>
      <div className="flex w-full items-center justify-between">
        <span
          className={cn(
            'inline-flex rounded-[6px] px-2 py-0.5 font-mono text-mono',
            // Dark mode collapses bg/subtle into bg/surface — see tokens.css.
            locked ? 'bg-subtle text-fg-muted dark:ring-1 dark:ring-line' : 'bg-indigo-soft text-indigo',
          )}
        >
          {profile.code}
        </span>
        {locked ? (
          <Lock aria-hidden="true" className="h-4 w-3.5 shrink-0 text-fg-muted" />
        ) : current ? (
          <span className="inline-flex rounded-pill bg-indigo px-2 py-0.5 text-label text-fg-inverse">
            Current
          </span>
        ) : null}
      </div>

      <div className={cn('flex w-full flex-col gap-1', locked && 'opacity-40')}>
        <p className="text-h3 text-fg">{profile.legalEntity}</p>
        <p className="text-label text-fg-muted">{profile.locality}</p>
        <div className="flex flex-wrap gap-1.5 pt-0.5">
          <Chip className={locked ? 'bg-page' : undefined}>{currencyChip(profile)}</Chip>
          <Chip className={locked ? 'bg-page' : undefined}>{profile.fiscalYear}</Chip>
          {/* No chip for a country without a tax identifier (CD — owner ruling N-53). */}
          {profile.taxLabel.trim() === '' ? null : (
            <Chip className={locked ? 'bg-page' : undefined}>{profile.taxLabel}</Chip>
          )}
        </div>
      </div>
    </>
  );

  if (locked) {
    return (
      <div
        className="flex h-[180px] w-full flex-col items-start justify-between rounded-md border border-dashed border-line bg-subtle p-4"
        aria-label={`${profile.name} — no access`}
      >
        {body}
        <p className="text-label text-fg-muted">No access</p>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onEnter(profile.code)}
      className={cn(
        'flex h-[180px] w-full flex-col items-start justify-between rounded-md bg-surface p-4 text-left transition-shadow hover:shadow-md',
        current ? 'border-[1.5px] border-indigo' : 'border border-line',
      )}
    >
      {body}
      <span className="inline-flex items-center gap-1 rounded-sm border border-line px-2.5 py-[5px] text-label font-medium text-indigo">
        Enter
        <ArrowRight aria-hidden="true" className="size-3.5" />
      </span>
    </button>
  );
}

/* ─────────────────────────────────────────────────── no-access full screen ── */

/**
 * ZERO ACCESSIBLE COUNTRIES.
 *
 * NOT IN FIGMA — the design's only "no access" is the locked TILE, which presumes
 * the user can enter something else. This state is composed from that tile's own
 * vocabulary (lock glyph, the administrator note, the ghost log-out) rather than
 * from new visual language.
 *
 * THE COPY NAMES NO COUNTRY. That is the correction the gate asked for: the mock
 * illustrated "no access" with Angola, a country whose team is a pilot for this
 * very phase, and a user in that team reading "you do not have access to Angola"
 * would reasonably conclude the app was broken rather than that their roles were
 * unassigned. The message describes the actual situation — an account with no
 * country role — and says who fixes it.
 */
function NoCompanyAccess({
  email,
  onSignOut,
}: {
  email: string | null;
  onSignOut: () => void;
}): React.JSX.Element {
  return (
    <div className="mx-auto flex w-full max-w-[440px] flex-col items-center gap-6 text-center">
      <G1Mark />
      <div className="flex flex-col items-center gap-3">
        <span className="inline-flex size-10 items-center justify-center rounded-full bg-fg-inverse/10 text-fg-inverse/80">
          <Lock aria-hidden="true" className="size-5" />
        </span>
        <h1 className="text-h1 text-fg-inverse">No company access yet</h1>
        <p className="text-body13 text-fg-inverse/70">
          Your sign-in worked, but this account has not been given access to any company yet. An
          administrator needs to assign you a country role before Global One has anything to show
          you.
        </p>
        {email === null ? null : (
          <p className="text-label text-fg-inverse/50">
            Signed in as <span className="font-mono text-mono text-fg-inverse/70">{email}</span>
          </p>
        )}
      </div>
      <div className="flex flex-col items-center gap-3.5">
        <p className="text-label text-fg-inverse/60">Your access is set by your administrator</p>
        <Button variant="ghost-on-dark" onClick={onSignOut}>
          Log out
        </Button>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── screen ── */

export function CountryPickerPage(): React.JSX.Element {
  const { me, accessibleCountries, isGroupAdmin, signOut } = useSession();
  const navigate = useNavigate();

  const accessible = new Set<CountryCode>(accessibleCountries);
  const total = COUNTRY_ORDER.length;

  // Exactly one choice is not a choice — go straight in.
  if (accessibleCountries.length === 1) {
    const only = accessibleCountries[0];
    if (only !== undefined) return <Navigate to={`/countries/${only}`} replace />;
  }

  const handleSignOut = (): void => {
    void signOut();
  };

  if (accessibleCountries.length === 0) {
    return (
      <EntryBackdrop>
        <NoCompanyAccess email={me?.email ?? null} onSignOut={handleSignOut} />
      </EntryBackdrop>
    );
  }

  const subtitle = isGroupAdmin
    ? 'You have access to every company'
    : `You have access to ${String(accessibleCountries.length)} of ${String(total)} companies`;

  return (
    <EntryBackdrop>
      <div className="mx-auto flex w-full max-w-[664px] flex-col items-center gap-10">
        <header className="flex flex-col items-center gap-5">
          <G1Mark />
          <div className="flex flex-col items-center gap-2 text-center">
            <h1 className="text-h1 text-fg-inverse">Choose your company</h1>
            <p className="text-label text-fg-inverse/70">{subtitle}</p>
          </div>
        </header>

        <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2">
          {COUNTRY_ORDER.map((code) => (
            <CountryTile
              key={code}
              profile={COUNTRIES[code]}
              locked={!accessible.has(code)}
              current={false}
              onEnter={(target) => {
                navigate(`/countries/${target}`);
              }}
            />
          ))}
        </div>

        <footer className="flex flex-col items-center gap-3.5">
          <p className="text-label text-fg-inverse/60">Your access is set by your administrator</p>
          <Button variant="ghost-on-dark" onClick={handleSignOut}>
            Log out
          </Button>
        </footer>
      </div>
    </EntryBackdrop>
  );
}
